import { useState, useEffect } from 'react';
import { 
  Users, Phone, TrendingUp, Calendar, Clock, CheckCircle,
  Target, Award, BarChart3, Activity, RefreshCw, Filter
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
// RingCentral API imports removed - using synced database table to avoid rate limits

interface RecruiterStats {
  recruiter_email: string;
  recruiter_name: string;
  recruiter_phone: string;
  total_leads: number;
  daily_leads: number;
  extra_leads: number;
  calls_today: number;
  calls_this_week: number;
  calls_this_month: number;
  leads_contacted: number;
  leads_hired: number;
  current_streak: number;
  avg_calls_per_day: number;
  conversion_rate: number;
  last_active: string;
}

export function RecruiterAdminDashboard() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [recruiters, setRecruiters] = useState<RecruiterStats[]>([]);
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'month'>('today');
  const [sortBy, setSortBy] = useState<'calls' | 'leads' | 'conversion'>('calls');

  useEffect(() => {
    loadRecruiterStats();
  }, [timeFilter]);

  const loadRecruiterStats = async () => {
    try {
      setLoading(true);

      // Using synced recruiter_calls table (data from RingCentral, no rate limits)
      console.log('📊 Loading call data from synced database (RingCentral data)...');

      // Get all recruiters with phone numbers from profiles table
      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('email, full_name, phone, ringcentral_extension_id')
        .eq('role', 'Recruiting');

      if (usersError) throw usersError;

      const stats: RecruiterStats[] = [];

      for (const user of users || []) {
        // Get lead counts
        const { count: totalLeads } = await supabase
          .from('crm_daily_assignments')
          .select('*', { count: 'exact', head: true })
          .eq('recruiter_email', user.email);

        const { count: dailyLeads } = await supabase
          .from('crm_daily_assignments')
          .select('*', { count: 'exact', head: true })
          .eq('recruiter_email', user.email)
          .eq('is_extra_request', false);

        const { count: extraLeads } = await supabase
          .from('crm_daily_assignments')
          .select('*', { count: 'exact', head: true })
          .eq('recruiter_email', user.email)
          .eq('is_extra_request', true);

        // Get call counts from synced recruiter_calls table (RingCentral data)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const { count: callsToday } = await supabase
          .from('recruiter_calls')
          .select('*', { count: 'exact', head: true })
          .eq('recruiter_email', user.email)
          .gte('start_time', today.toISOString());

        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        
        const { count: callsWeek } = await supabase
          .from('recruiter_calls')
          .select('*', { count: 'exact', head: true })
          .eq('recruiter_email', user.email)
          .gte('start_time', weekAgo.toISOString());

        const monthAgo = new Date();
        monthAgo.setDate(monthAgo.getDate() - 30);
        
        const { count: callsMonth } = await supabase
          .from('recruiter_calls')
          .select('*', { count: 'exact', head: true })
          .eq('recruiter_email', user.email)
          .gte('start_time', monthAgo.toISOString());

        console.log(`👤 ${user.full_name} - Today: ${callsToday} | Week: ${callsWeek} | Month: ${callsMonth}`);

        // Get contacted and hired counts
        const { data: leads } = await supabase
          .from('crm_daily_assignments')
          .select('crm_leads(status, contact_attempts)')
          .eq('recruiter_email', user.email);

        const contacted = leads?.filter((l: any) => l.crm_leads?.contact_attempts > 0).length || 0;
        const hired = leads?.filter((l: any) => l.crm_leads?.status === 'hired').length || 0;

        const conversionRate = totalLeads ? (hired / totalLeads) * 100 : 0;
        const avgCallsPerDay = callsWeek ? callsWeek / 7 : 0;

        stats.push({
          recruiter_email: user.email,
          recruiter_name: user.full_name,
          recruiter_phone: user.phone || 'N/A',
          total_leads: totalLeads || 0,
          daily_leads: dailyLeads || 0,
          extra_leads: extraLeads || 0,
          calls_today: callsToday || 0,
          calls_this_week: callsWeek || 0,
          calls_this_month: callsMonth || 0,
          leads_contacted: contacted,
          leads_hired: hired,
          current_streak: 0, // TODO: Calculate from daily call history
          avg_calls_per_day: Math.round(avgCallsPerDay),
          conversion_rate: Math.round(conversionRate * 10) / 10,
          last_active: new Date().toISOString()
        });
      }

      // Sort recruiters
      stats.sort((a, b) => {
        if (sortBy === 'calls') return b.calls_today - a.calls_today;
        if (sortBy === 'leads') return b.total_leads - a.total_leads;
        return b.conversion_rate - a.conversion_rate;
      });

      setRecruiters(stats);
    } catch (error) {
      console.error('Error loading recruiter stats:', error);
      showToast('error', 'Failed to load recruiter statistics');
    } finally {
      setLoading(false);
    }
  };

  // Calculate team totals
  const teamStats = {
    totalCalls: recruiters.reduce((sum, r) => sum + r.calls_today, 0),
    totalLeads: recruiters.reduce((sum, r) => sum + r.total_leads, 0),
    totalHired: recruiters.reduce((sum, r) => sum + r.leads_hired, 0),
    avgConversion: recruiters.length ? recruiters.reduce((sum, r) => sum + r.conversion_rate, 0) / recruiters.length : 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-xl">
        <div className="max-w-full mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">
                Recruiter Performance Dashboard
              </h1>
              <p className="text-blue-100 text-sm font-medium">
                Admin & SuperAdmin View - Monitor team performance and metrics
              </p>
            </div>
            
            <button
              onClick={loadRecruiterStats}
              className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-semibold shadow-lg"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto px-6 py-6">
        {/* Team Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">Today</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 mb-1">{teamStats.totalCalls}</p>
            <p className="text-sm text-slate-600">Total Calls Made</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">Active</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 mb-1">{teamStats.totalLeads}</p>
            <p className="text-sm text-slate-600">Total Leads Assigned</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">Success</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 mb-1">{teamStats.totalHired}</p>
            <p className="text-sm text-slate-600">Drivers Hired</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">Avg</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 mb-1">{teamStats.avgConversion.toFixed(1)}%</p>
            <p className="text-sm text-slate-600">Conversion Rate</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-slate-600" />
              <span className="text-sm font-semibold text-slate-700">Sort by:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSortBy('calls')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    sortBy === 'calls'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Calls
                </button>
                <button
                  onClick={() => setSortBy('leads')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    sortBy === 'leads'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Leads
                </button>
                <button
                  onClick={() => setSortBy('conversion')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    sortBy === 'conversion'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Conversion
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-600" />
              <span className="text-sm font-semibold text-slate-700">Time:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setTimeFilter('today')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    timeFilter === 'today'
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setTimeFilter('week')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    timeFilter === 'week'
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  This Week
                </button>
                <button
                  onClick={() => setTimeFilter('month')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    timeFilter === 'month'
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  This Month
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Recruiter Performance Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900">Individual Recruiter Performance</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Recruiter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500">Phone</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500">Calls/Day</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500">Calls/Week</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500">Calls/Month</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500">Total Leads</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500">Contacted</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500">Hired</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500">Conversion</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500">Streak</th>
                </tr>
              </thead>
              <tbody>
                {recruiters.map((recruiter, index) => (
                  <tr key={recruiter.recruiter_email} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-orange-600' : 'bg-blue-600'
                        }`}>
                          {recruiter.recruiter_name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{recruiter.recruiter_name}</div>
                          <div className="text-xs text-slate-500">{recruiter.recruiter_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-700">{recruiter.recruiter_phone}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`text-lg font-bold ${
                        recruiter.calls_today >= 40 ? 'text-green-600' : recruiter.calls_today >= 20 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {recruiter.calls_today}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-slate-700">{recruiter.calls_this_week}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-blue-600">{recruiter.calls_this_month}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-slate-700">{recruiter.total_leads}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-blue-600">{recruiter.leads_contacted}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-green-600">{recruiter.leads_hired}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`text-sm font-bold ${
                        recruiter.conversion_rate >= 5 ? 'text-green-600' : recruiter.conversion_rate >= 2 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {recruiter.conversion_rate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Award className={`w-4 h-4 ${recruiter.current_streak > 0 ? 'text-orange-500' : 'text-slate-300'}`} />
                        <span className="text-sm font-medium text-slate-700">{recruiter.current_streak}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
